/* eslint-disable react/jsx-curly-newline */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Icon,
  Loader,
  Popover,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import {
  individualBulkAction,
  individualTreeBulkAction,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setIsARSModalOpen,
  setSidePanelVisible,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_ROLEOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  DATE_COLUMNS,
  GROUP_BY_APP_VALUE,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_SIZE_TABLET_SMALL,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import {
  getMessageKey,
  getRiskMeterParamsForConfidence,
  getStateForPAA,
  getTrustChipIconKind,
} from '../../../utils/utils';
import {
  AccessRequestHistoryButton,
  ARS_ID_FOR_EVENT,
  ARS_KEYS,
} from '../../AccessRequestHistory';
import CertsActionButtonsGroup, {
  ACTION_BUTTON_GROUP_KINDS,
} from '../../CertsActionButtons';
import RiskScore from '../../RiskScore';
import {
  ACC_CERT_ENTITLEMENT_VALUEKEY,
  COL_FILTER_HEADER_ACCOUNT,
  USR_CERT_ENTITLEMENT_VALUEKEY,
} from '../../ViewControls';

import {
  CERT_ENTITLEMENT_VALUEKEY,
  COL_FILTER_HEADER_ENTITLEMENT,
  COL_FILTER_HEADER_ROLE,
} from './constant';
import TrustJustification from './TrustJustification';
import {
  disableAllConnectedBar,
  getCertKind,
  getEOCertKind,
  getEOFixedColKey,
  getFixedColKey,
  getLoadingFlag,
  getROCertKind,
  getROFixedColKey,
  markChildrenAsParent,
  // markChildrenAsParent,
  markParentBasedOnChildren,
  scrollToSelectedTile,
  TITLE_COL_MESSAGE_KEYS,
  toggleConnectedTiles,
} from './utils';

import './Tile.css';

function ViewportTypography({
  children,
  kind,
  color,
  tooltipConfigs,
  lineLimit,
}) {
  const [isClipped, setIsClipped] = React.useState(false);
  const textRef = React.useRef(null);

  const handleMouseEnter = () => {
    const el = textRef.current;

    if (!el) return;

    const textEl = el.querySelector('[class*="Typography"]');

    if (!textEl) return;

    const textContent = textEl.textContent?.trim();

    if (!textContent || textContent === '-') return;

    const overflowParent = el.closest('.Tile-attributeCols');

    if (!overflowParent) return;

    const textRect = textEl.getBoundingClientRect();
    const parentRect = overflowParent.getBoundingClientRect();
    const clipped =
      textRect.right > parentRect.right ||
      textRect.left < parentRect.left ||
      textEl.scrollWidth > textEl.clientWidth;

    setIsClipped(clipped);
  };

  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <div ref={textRef} onMouseEnter={handleMouseEnter} style={{ minWidth: 0 }}>
      <Typography
        kind={kind}
        color={color}
        tooltipConfigs={tooltipConfigs}
        lineLimit={lineLimit}
        showTooltipOnHover={isClipped}>
        {children}
      </Typography>
    </div>
  );
}

ViewportTypography.propTypes = {
  children: PropTypes.node,
  kind: PropTypes.string,
  color: PropTypes.string,
  tooltipConfigs: PropTypes.shape({}),
  lineLimit: PropTypes.number,
};

ViewportTypography.defaultProps = {
  children: null,
  kind: null,
  color: null,
  tooltipConfigs: null,
  lineLimit: null,
};

const TOOLTIP_DELAY_IN_MS_TILE = 500;
const SCROLL_THRESHOLD = 10;
const EXPAND_ICON_PATH =
  'M14.4128 7.64661C14.6081 7.45138 14.9246 7.45141 15.1199 7.64661L19.0378 11.5626C19.0746 11.5994 19.1033 11.6422 19.1267 11.6876C19.1329 11.6996 19.14 11.7112 19.1453 11.7238C19.1465 11.7266 19.1471 11.7297 19.1482 11.7325C19.1708 11.7895 19.1843 11.8512 19.1843 11.9161C19.1843 11.9985 19.162 12.0752 19.1267 12.1437C19.1032 12.1894 19.0748 12.2327 19.0378 12.2697L15.1199 16.1857C14.9246 16.3809 14.6081 16.3809 14.4128 16.1857C14.2179 15.9904 14.2177 15.6738 14.4128 15.4786L17.3103 12.5831H6.52222L9.58667 15.6466C9.7819 15.8418 9.78187 16.1584 9.58667 16.3536C9.39142 16.5486 9.07484 16.5488 8.87964 16.3536L4.96167 12.4376C4.86801 12.344 4.8153 12.2166 4.81519 12.0841C4.81518 11.9515 4.86791 11.8234 4.96167 11.7296L8.87964 7.8136C9.07484 7.61847 9.39142 7.61859 9.58667 7.8136C9.78187 8.00888 9.78191 8.3254 9.58667 8.52063L6.69116 11.4161H17.4773L14.4128 8.35364C14.2177 8.15849 14.2179 7.8419 14.4128 7.64661Z';

// Persistent storage for expanded tiles state using sessionStorage
const getExpandedTilesKey = () => 'expandedTiles_mobile';

const getExpandedState = (key) => {
  try {
    const stored = sessionStorage.getItem(getExpandedTilesKey());
    const expandedTiles = stored ? JSON.parse(stored) : {};

    return expandedTiles[key] || false;
  } catch {
    return false;
  }
};

const setExpandedState = (key, isExpanded) => {
  try {
    const stored = sessionStorage.getItem(getExpandedTilesKey());
    const expandedTiles = stored ? JSON.parse(stored) : {};

    if (isExpanded) {
      expandedTiles[key] = true;
    } else {
      delete expandedTiles[key];
    }

    sessionStorage.setItem(
      getExpandedTilesKey(),
      JSON.stringify(expandedTiles)
    );
  } catch {
    // Ignore storage errors
  }
};

function Tile({
  data,
  level,
  handleSidePanel,
  handleAutoSelectParent,
  nodeData,
  setMergedLists,
  actionPopoverOpenId,
  setActionPopoverOpenId,
  isScrolling,
  columnWidths = {},
  onResizeColumn = () => {},
  showResizeHandles = false,
  tileLevel,
  isFirstTile = false,
  isFirstGroup = false,
  isGroupByActive = false,
  onTitleExpand = () => {},
}) {
  const getDefaultTitleWidth = () => {
    if (tileLevel === 0) return 304;
    if (tileLevel === 1) return 272;

    return 240;
  };

  const MIN_TITLE_WIDTH = 200;
  const defaultTitleWidth = getDefaultTitleWidth();

  // Create stable key early for expanded state
  const stableKey = `${
    nodeData?.id ||
    nodeData?.CERT_USERKEY ||
    nodeData?.CERT_ENTITLEMENT_VALUEKEY ||
    nodeData?.CERT_ROLEKEY ||
    nodeData?.tracker?.id
  }`;

  // isExpanded
  const [isCheckingIntoSession, setIsCheckingIntoSession] = useState(false);
  const [isActionGroupExpanded, setIsActionGroupExpanded] = useState(false);
  const [isScoreMeterPopoverOpen, setIsScoreMeterPopoverOpen] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const [isTouchScrolling, setIsTouchScrolling] = useState(false);
  const [touchStartPosition, setTouchStartPosition] = useState({ x: 0, y: 0 });
  const [isTitleExpanded, setIsTitleExpanded] = useState(() =>
    getExpandedState(stableKey)
  );
  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  // Column resize state and refs
  const [resizingCol, setResizingCol] = useState(null);
  const tileRef = useRef(null);
  const titleColRef = useRef(null);
  const justResizedRef = useRef(false); // Prevents side panel from opening immediately after resize
  // Mobile expand/collapse: stores the calculated height adjustment for proper collapse restoration
  // COACH MARK DISABLED - Uncomment below to re-enable
  // const [showCoachMark, setShowCoachMark] = useState(false);
  // const hasScrolledRef = useRef(false);
  const lastAdjustmentRef = useRef(0);
  const dispatch = useDispatch();
  const intl = useIntl();

  const { messages } = useSelector((state) => state?.user);
  const {
    bulkAction: {
      isAtleastOneSelected,
      isBulkActionEnabled,
      isChecked: isBulkChecked,
      isDisabled: isBulkDisabled,
      isSubmittingAction,
    },
    core: { configMap, isLoading, isTrustScoreEnabled, campaignType },
    global: {
      certKey,
      columnLabelKeys,
      columnPickerList,
      globalDateFormat,
      mediaQueryValue,
    },
    reviewAndSubmit: { shouldShowReviewAndSubmitButton },
    sidePanel: { isSidePanelVisible },
    viewControls: { groupByOptions },
  } = useSelector((state) => state?.certifications?.details);

  // Tile Scoped Variables
  const isTileSidepanelSelected =
    nodeData?.tracker?.isConnected && isSidePanelVisible;
  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isROCampaign = CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ROLEOWNER;
  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const getCertsKindByCampaign = () => {
    if (isEOCampaign) return getEOCertKind(nodeData);
    if (isROCampaign) return getROCertKind(nodeData);

    return getCertKind(nodeData);
  };

  const getFixedColKeyByCampaign = (kind) => {
    if (isEOCampaign) return getEOFixedColKey(kind);
    if (isROCampaign) return getROFixedColKey(kind);

    return getFixedColKey(kind);
  };

  const certsKind = getCertsKindByCampaign();
  const fixedColKey = getFixedColKeyByCampaign(certsKind);

  const actionButtonsMap =
    nodeData?.availableAction && Object.keys(nodeData?.availableAction);
  const trackerId = nodeData?.tracker?.id;
  const isLevelZero = level === 0;
  const isLevelOne = level === 1;
  const isLevelTwo = level === 2;
  const isEndPointGroup = nodeData?.isEndPoint && isLevelZero;
  const isCheckboxVisible = Boolean(
    isBulkActionEnabled &&
      nodeData?.enableAction?.length > 0 &&
      !isEndPointGroup &&
      (isCheckingIntoSession ||
        nodeData?.tracker?.isChecked ||
        nodeData?.tracker?.isIndeterminate ||
        isAtleastOneSelected)
  );
  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;
  const isThisTilesActionPopoverOpen = actionPopoverOpenId === trackerId;
  const shouldDisableWhileCallingActionApis = getLoadingFlag(
    nodeData?.tracker?.actions
  );
  const isCertsKindUser =
    nodeData?.CERT_ENTITLEMENT_VALUEKEY === CERT_ENTITLEMENT_VALUEKEY.USER;
  const shouldDisableActionBtns = Boolean(
    isAtleastOneSelected ||
      isBulkChecked ||
      isBulkDisabled ||
      isSubmittingAction ||
      isCheckingIntoSession ||
      shouldDisableWhileCallingActionApis
  );
  const shouldShowGroupToolTip = Boolean(
    isAtleastOneSelected ||
      isBulkChecked ||
      (isBulkDisabled && !isSubmittingAction)
  );
  const isSaviResponded = Boolean(
    nodeData?.savi_responded && nodeData?.savi_responded === '1'
  );
  const saviRespondedReason = [];

  if (
    isSaviResponded &&
    nodeData?.savi_reason &&
    nodeData?.savi_reason.length > 0
  )
    nodeData?.savi_reason.map((item) => saviRespondedReason.push(item));

  const tabletSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_SMALL
  );
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isTabletWidth = tabletSmallQuery.value <= mediaQueryValue;
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const isTrustChipVisible =
    isMobileXlargeWidth &&
    isTrustScoreEnabled &&
    (certsKind === COL_FILTER_HEADER_ENTITLEMENT ||
      certsKind === COL_FILTER_HEADER_ROLE);

  // Conditional Classes
  const tileClasses = classnames(
    'Tile',
    `Tile--${level}`,
    isSidePanelVisible && 'Tile--isSidePanelVisible',
    isTileSidepanelSelected && 'Tile--isTileSidepanelSelected',
    isStuck && 'Tile--isStuck',
    isThisTilesActionPopoverOpen && 'Tile--isActionPopoverOpen',
    isTrustScoreEnabled && 'Tile--isTrustScoreEnabled',
    isEndPointGroup && 'Tile--isEndPointGroup',
    isUsingGroupBy && 'Tile--isUsingGroupBy',
    isMobileXlargeWidth && isTitleExpanded && 'Tile--isTitleExpanded'
  );

  const leftHoverSideClasses = classnames(
    'Tile-leftHoverSide',
    `Tile-leftHoverSide--${level}`,
    nodeData?.tracker?.isConnected && 'Tile-leftHoverSide--isSidePanelOpen'
  );
  // Within Hover Area
  const tileCheckHoverDisplayBtn = classnames(
    'Tile-checkboxHoverDisplay',
    isBulkDisabled && 'Tile-checkboxHoverDisplay--disabled'
  );

  const tileCheckIconClasses = classnames('Tile-icon');

  const tileCheckboxClasses = classnames(
    'Tile-checkbox',
    isCheckboxVisible && 'Tile-checkbox--isCheckboxVisible'
  );
  const tileCheckboxIconClasses = classnames(
    'Tile-checkboxIcon',
    isCheckboxVisible && 'Tile-checkboxIcon--isCheckboxVisible'
  );
  const tileCheckboxAvatarClasses = classnames(
    'Tile-checkboxIcon',
    'Tile-checkboxAvatar',
    isCheckboxVisible && 'Tile-checkboxIcon--isCheckboxVisible'
  );
  const actionButtonGroupClasses = classnames(
    'Tile-actionButtonGroup',
    isActionGroupExpanded && 'Tile-actionButtonGroup--isExpanded',
    actionButtonsMap?.length &&
      `Tile-actionButtonGroup--length${actionButtonsMap.length}`
  );
  const titleColClasses = classnames(
    'Tile-titleCol',
    isMobileXlargeWidth && isTextOverflowing && 'Tile-titleCol--hasOverflow'
  );

  const buildThresholdList = () => {
    const thresholds = [];

    for (let i = 0; i <= 1.0; i += 0.01) {
      thresholds.push(i);
    }

    return thresholds;
  };

  // COACH MARK DISABLED - Uncomment below to re-enable
  // Coach mark for resize handle - check cookie on mount
  // useEffect(() => {
  //   if (
  //     showResizeHandles &&
  //     (isFirstTile || isFirstGroup) &&
  //     !isMobileXlargeWidth
  //   ) {
  //     const hasSeenCoachMark = document.cookie.includes(
  //       'resize-coach-mark-seen=true'
  //     );

  //     if (!hasSeenCoachMark) {
  //       setShowCoachMark(true);
  //     }
  //   }
  // }, [showResizeHandles, isFirstTile, isFirstGroup, isMobileXlargeWidth]);

  // Hide coach mark when tile becomes sticky
  // useEffect(() => {
  //   const hasSeenCoachMark = document.cookie.includes(
  //     'resize-coach-mark-seen=true'
  //   );

  //   if (!hasSeenCoachMark) {
  //     const certsDetailsList = document.getElementById('certsDetailsList');
  //     const isScrollAtTop = certsDetailsList?.scrollTop === 0;

  //     if (isScrolling) {
  //       hasScrolledRef.current = true;
  //       setShowCoachMark(false);
  //     } else if (
  //       isScrollAtTop &&
  //       !isStuck &&
  //       (isFirstTile || isFirstGroup) &&
  //       showResizeHandles &&
  //       !isMobileXlargeWidth
  //     ) {
  //       setShowCoachMark(true);
  //     } else if (isStuck && showResizeHandles && !isMobileXlargeWidth) {
  //       setShowCoachMark(true);
  //     } else if (
  //       !hasScrolledRef.current &&
  //       !isStuck &&
  //       (isFirstTile || isFirstGroup) &&
  //       showResizeHandles &&
  //       !isMobileXlargeWidth
  //     ) {
  //       setShowCoachMark(true);
  //     } else if (hasScrolledRef.current && !isStuck && !isScrollAtTop) {
  //       setShowCoachMark(false);
  //     }
  //   }
  // }, [
  //   isScrolling,
  //   isStuck,
  //   showResizeHandles,
  //   isFirstTile,
  //   isFirstGroup,
  //   isMobileXlargeWidth,
  // ]);

  // const handleCoachMarkDismiss = (e) => {
  //   e.stopPropagation();
  //   document.cookie = 'resize-coach-mark-seen=true; path=/; max-age=31536000';
  //   setShowCoachMark(false);
  // };

  useEffect(() => {
    setMergedLists((prevMergedLists) =>
      markParentBasedOnChildren(prevMergedLists)
    );

    const stickyContainer = tileRef.current;
    const certsDetailsList = document.getElementById('certsDetailsList');

    if (!stickyContainer || isLevelTwo) return;
    // Debug background
    // stickyContainer.style.backgroundColor = 'rgba(255, 0, 0, 0.3)';

    let customRootMargin;
    let levelOneRootMargin = '-65px 0px 500px 0px';

    if (isMobileXlargeWidth && !isUsingGroupBy)
      levelOneRootMargin = '-49px 0px 500px 0px';
    if (isUsingGroupBy) levelOneRootMargin = '-37px 0px 500px 0px';

    if (isLevelZero) customRootMargin = '-1px 0px 500px 0px';
    if (isLevelOne) customRootMargin = levelOneRootMargin;

    const options = {
      root: certsDetailsList,
      rootMargin: customRootMargin,
      threshold: buildThresholdList(),
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        let isCurrentlyStuck;

        if (isMobileXlargeWidth) {
          // Mobile: Check position instead of ratio to handle dynamic height
          const rect = entry.boundingClientRect;
          let stickyTop;

          if (isLevelZero) stickyTop = 0;
          else if (isLevelOne) {
            stickyTop = !isUsingGroupBy ? 48 : 36;
          }

          isCurrentlyStuck =
            rect.top <= stickyTop + 1 && entry.intersectionRatio < 1;
        } else {
          // Desktop: Use intersection ratio
          isCurrentlyStuck =
            entry.intersectionRatio > 0 && entry.intersectionRatio < 1;
        }

        setIsStuck(isCurrentlyStuck);
      });
    }, options);

    observer.observe(stickyContainer);

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isSidePanelVisible) {
      setIsScoreMeterPopoverOpen(false);
      // Adjust column widths when side panel opens

      if (tileRef.current) {
        const leftHoverSide = tileRef.current.querySelector(
          '.Tile-leftHoverSide'
        );
        const attributeCols = tileRef.current.querySelector(
          '.Tile-attributeCols'
        );

        if (
          leftHoverSide &&
          titleColRef.current &&
          columnWidths[0] !== undefined
        ) {
          const availableWidth = leftHoverSide.offsetWidth;
          const maxTitleWidth = Math.min(
            availableWidth - 100,
            defaultTitleWidth + columnWidths[0]
          );

          titleColRef.current.style.width = `${maxTitleWidth}px`;
        }

        // Adjust attribute columns to fit in shrunk container
        if (attributeCols) {
          const attributeElements =
            attributeCols.querySelectorAll('.Tile-attribute');
          // const availableWidth = attributeCols.offsetWidth;

          attributeElements.forEach((attr, index) => {
            const colIndex = index + 1;

            if (columnWidths[colIndex]) {
              const rect = attr.getBoundingClientRect();
              const colsRect = attributeCols.getBoundingClientRect();
              const remainingSpace = colsRect.right - rect.left;
              const maxWidth = Math.min(
                columnWidths[colIndex],
                remainingSpace - 40
              );

              // eslint-disable-next-line no-param-reassign
              attr.style.width = `${Math.max(100, maxWidth)}px`;
            }
          });
        }
      }
    } else {
      setMergedLists((prev) => disableAllConnectedBar(prev));
      // Restore original widths when side panel closes

      if (tileRef.current) {
        if (titleColRef.current && columnWidths[0] !== undefined) {
          titleColRef.current.style.width = `${
            defaultTitleWidth + columnWidths[0]
          }px`;
        }

        const attributeCols = tileRef.current.querySelector(
          '.Tile-attributeCols'
        );

        if (attributeCols) {
          const attributeElements =
            attributeCols.querySelectorAll('.Tile-attribute');

          attributeElements.forEach((attr, index) => {
            const colIndex = index + 1;

            if (columnWidths[colIndex]) {
              // eslint-disable-next-line no-param-reassign
              attr.style.width = `${columnWidths[colIndex]}px`;
            }
          });
        }
      }
    }
  }, [isSidePanelVisible, columnWidths, defaultTitleWidth]);

  useEffect(() => {
    if (isActionGroupExpanded) setIsActionGroupExpanded(false);
    // COACH MARK DISABLED - Uncomment below to re-enable
    // if (isScrolling) setShowCoachMark(false);
  }, [isScrolling, isMobileXlargeWidth]);

  // Sync with sessionStorage on mount
  useEffect(() => {
    const storedState = getExpandedState(stableKey);

    if (storedState !== isTitleExpanded) {
      setIsTitleExpanded(storedState);
    }
  }, [stableKey]);

  useEffect(() => {
    if (isTileSidepanelSelected) scrollToSelectedTile(tileRef);
  }, [isTileSidepanelSelected]);

  const isNoValueTitle =
    nodeData[fixedColKey] === null ||
    nodeData[fixedColKey] === undefined ||
    nodeData?.[fixedColKey]?.trim()?.length === 0;
  const titleValue = isNoValueTitle ? 'Not Provided' : nodeData[fixedColKey];

  // Mobile expand/collapse: detects if title text is overflowing to show expand button
  useEffect(() => {
    if (!isMobileXlargeWidth) {
      setIsTextOverflowing(false);

      return;
    }

    const checkOverflow = () => {
      if (!titleColRef.current) return;

      const h3Element = titleColRef.current.querySelector('h3');

      if (!h3Element) return;

      // For user and role tiles (level 0 and 1), always check for overflow
      // For other levels, use existing logic
      if (level === 0 || level === 1) {
        const isOverflowing = h3Element.scrollWidth > h3Element.clientWidth + 1;

        setIsTextOverflowing(isOverflowing || isTitleExpanded);
      } else if (isTitleExpanded) {
        setIsTextOverflowing(true);
      } else {
        const isOverflowing =
          h3Element.scrollWidth > h3Element.clientWidth + 1 ||
          h3Element.scrollHeight > h3Element.clientHeight + 2;

        setIsTextOverflowing(isOverflowing);
      }
    };

    setTimeout(checkOverflow, 0);
  }, [isMobileXlargeWidth, titleValue, columnWidths, isTitleExpanded, level]);

  // Mobile expand/collapse: dynamically adjusts parent tree heights based on actual text content
  // Calculates height based on scrollHeight when expanding, uses stored value when collapsing
  useEffect(() => {
    if (!isMobileXlargeWidth || !tileRef.current || !titleColRef.current)
      return;

    const cardsTree = tileRef.current.closest('.Cards-tree');

    if (!cardsTree) return;

    const allParentTrees = [];
    let currentTree = cardsTree;

    while (currentTree) {
      allParentTrees.push(currentTree);
      currentTree = currentTree.parentElement?.closest('.Cards-tree');
    }

    const applyHeightAdjustment = (adjustment) => {
      allParentTrees.forEach((treeElement) => {
        const computedHeight = window.getComputedStyle(treeElement).height;
        const heightInPx = parseFloat(computedHeight);
        const remSize = parseFloat(
          getComputedStyle(document.documentElement).fontSize
        );
        const heightInRem = heightInPx / remSize;
        const newHeight = `${heightInRem + adjustment}rem`;

        // eslint-disable-next-line no-param-reassign
        treeElement.style.height = newHeight;
      });
    };

    if (isTitleExpanded) {
      const h3Element = titleColRef.current.querySelector('h3');

      if (h3Element) {
        setTimeout(() => {
          const expandedHeight = h3Element.scrollHeight;
          const collapsedHeight =
            parseFloat(getComputedStyle(h3Element).lineHeight) || 24;
          const extraLines = Math.ceil(
            (expandedHeight - collapsedHeight) / collapsedHeight
          );

          const adjustment = Math.max(2.25, extraLines * 1.5);

          lastAdjustmentRef.current = adjustment;
          applyHeightAdjustment(adjustment);
        }, 0);
      }
    } else if (lastAdjustmentRef.current > 0) {
      const adjustment = -lastAdjustmentRef.current;

      lastAdjustmentRef.current = 0;
      applyHeightAdjustment(adjustment);
    }
  }, [isTitleExpanded, isMobileXlargeWidth]);

  const handleSidePanelOpening = useCallback(
    (id, connectedBarLevel) => {
      handleSidePanel(nodeData);

      setMergedLists((prevMergedLists) =>
        toggleConnectedTiles(prevMergedLists, id, connectedBarLevel)
      );
    },
    [handleSidePanel, nodeData]
  );

  const handleTileClick = (e) => {
    // Prevent tile click during or immediately after column resize
    if (resizingCol || justResizedRef.current) return;

    if (e?.target?.parentNode?.id === ARS_ID_FOR_EVENT) {
      const payload = {
        ARSTASKKEY: nodeData?.ARSTASKKEY,
        ARSTASKKEYFLAG: nodeData?.arstaskkeyflag,
        CERT_ENTITLEMENT_VALUEKEY: nodeData?.CERT_ENTITLEMENT_VALUEKEY,
        CERT_USERKEY: nodeData?.CERT_USERKEY,
        CERT_ROLEKEY: nodeData?.CERT_ROLEKEY,
        CERT_ACCOUNTKEY: nodeData?.CERT_ACCOUNTKEY,
      };

      dispatch(setIsARSModalOpen(true, payload));

      return;
    }

    handleSidePanelOpening(trackerId, nodeData.tracker.level);
  };

  const handleKeyDownTile = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTileClick();
    }
  };

  const handleTileCheck = (prev, id, checkLevel) => {
    const prevMergedLists = prev.map((item) => {
      if (id === null || checkLevel === null) return item;

      if (item.tracker.level !== checkLevel) {
        return {
          ...item,
          children:
            item?.children?.length > 0
              ? handleTileCheck(item.children, id, checkLevel)
              : [],
        };
      }

      if (item.tracker.id === id) {
        return {
          ...item,
          tracker: {
            ...item.tracker,
            isChecked: !item.tracker.isChecked,
            isIndeterminate: false,
          },
          children:
            item?.children?.length > 0
              ? markChildrenAsParent(item.children, !item.tracker.isChecked)
              : [],
        };
      }

      return item;
    });

    return markParentBasedOnChildren(prevMergedLists);
  };

  const handleCheckboxChange = (
    id,
    checkBoxLevel,
    currentStatus,
    hasChildren
  ) => {
    dispatch(setSidePanelVisible(false));
    setIsCheckingIntoSession(true);

    if (
      ((nodeData.CERT_ENTITLEMENT_VALUEKEY === USR_CERT_ENTITLEMENT_VALUEKEY ||
        nodeData.CERT_ENTITLEMENT_VALUEKEY === ACC_CERT_ENTITLEMENT_VALUEKEY) &&
        hasChildren) ||
      (isEOCampaign && nodeData.ROW_TYPE?.toString() === '0')
    ) {
      const userAccountKeys = {};

      if (isEOCampaign) {
        userAccountKeys.cert_entitlement_valuekey =
          nodeData?.CERT_ENTITLEMENT_VALUEKEY;
      } else {
        if (
          nodeData.CERT_ENTITLEMENT_VALUEKEY === USR_CERT_ENTITLEMENT_VALUEKEY
        ) {
          userAccountKeys.cert_userkey = nodeData.CERT_USERKEY;
        }

        if (
          nodeData.CERT_ENTITLEMENT_VALUEKEY === ACC_CERT_ENTITLEMENT_VALUEKEY
        ) {
          userAccountKeys.cert_userkey = nodeData.CERT_USERKEY;
          userAccountKeys.cert_accountkey = nodeData.CERT_ACCOUNTKEY;
        }
      }

      dispatch(individualTreeBulkAction(userAccountKeys, currentStatus))
        .then(() => {
          dispatch(setBulkActionBySelectAll(false));
          setMergedLists((prevMergedLists) =>
            handleTileCheck(prevMergedLists, id, checkBoxLevel)
          );
        })
        .catch((error) => {
          dispatch(setBulkActionAlert(true, error.message));
        })
        .finally(() => {
          setIsCheckingIntoSession(false);
        });
    } else {
      dispatch(
        individualBulkAction(
          certKey,
          nodeData?.id,
          !currentStatus,
          nodeData?.unique_id
        )
      )
        .then(() => {
          dispatch(setBulkActionBySelectAll(false));
          setMergedLists((prevMergedLists) =>
            handleTileCheck(prevMergedLists, id, checkBoxLevel)
          );
        })
        .catch((error) => {
          dispatch(setBulkActionAlert(true, error.message));
        })
        .finally(() => {
          setIsCheckingIntoSession(false);
        });
    }
  };

  const getTileCheckIcon = (isDrawer) => {
    if (isEndPointGroup)
      return (
        <Icon
          kind='Applications'
          color='--color-foreground-secondary-bold'
          size={isMobileXlargeWidth ? 'medium' : 'smallMedium'}
          className={tileCheckboxIconClasses}
        />
      );

    if (isCertsKindUser)
      return (
        <Avatar
          size={isMobileXlargeWidth && isDrawer ? 'xSmall' : 'small'}
          className={tileCheckboxAvatarClasses}
        />
      );

    // to get the icons for each tile
    const getCertIconKind = () => {
      if (isEOCampaign) {
        return getEOCertKind(nodeData, true);
      }

      if (isROCampaign) {
        return getROCertKind(nodeData);
      }

      return getCertKind(nodeData);
    };

    return (
      <Icon
        kind={getCertIconKind()}
        color='--color-foreground-secondary-bold'
        size={isMobileXlargeWidth ? 'medium' : 'smallMedium'}
        className={tileCheckboxIconClasses}
      />
    );
  };

  const handleCheckboxHoverDisplay = () => {
    const shouldDisableTileCheckBoxes = Boolean(
      !isBulkActionEnabled ||
        isBulkDisabled ||
        isEndPointGroup ||
        isSubmittingAction ||
        !shouldShowReviewAndSubmitButton ||
        isLoading ||
        (nodeData?.enableAction?.length === 0 &&
          Object.keys(nodeData?.availableAction)?.length !== 0)
    );
    const isCheckBoxChecked = Boolean(
      isBulkActionEnabled &&
        (nodeData?.tracker?.isChecked ||
          (nodeData?.enableAction?.length === 0 &&
            Object.keys(nodeData?.availableAction)?.length !== 0))
    );

    return (
      <>
        <Box tag='div' className={tileCheckboxClasses}>
          {!isCheckingIntoSession ? (
            <Checkbox
              id='tile-checkbox'
              name='checkbox-no-hovereffect'
              showHoverEffect
              dataTestId='checkbox-no-hovereffect'
              isChecked={isCheckBoxChecked}
              isDisabled={shouldDisableTileCheckBoxes}
              isIndeterminate={nodeData?.tracker?.isIndeterminate}
              onChange={() =>
                handleCheckboxChange(
                  nodeData?.tracker?.id,
                  nodeData?.tracker?.level,
                  nodeData?.tracker?.isChecked,
                  nodeData?.tracker?.childCount > 0
                )
              }
            />
          ) : (
            <Loader kind='loop' format='inline' color='brand' size='small' />
          )}
        </Box>
        {!isCheckBoxChecked && getTileCheckIcon(false)}
      </>
    );
  };

  const getTileKindHeading = () =>
    messages[TITLE_COL_MESSAGE_KEYS[fixedColKey]] ||
    localize(intl, getMessageKey(TITLE_COL_MESSAGE_KEYS[fixedColKey]));

  // TODO: Currently there are duplicated usage of risk variables. Consider the implementation of each variable and combine what can be reused vars: riskScore, riskKind, riskLabel.
  const getRiskChip = () => {
    const chipRiskScore = Number(nodeData?.JRMRULES?.cnf || 0);

    if (isTrustScoreEnabled?.enableCertificationTrustModel) {
      const upperLimitValue =
        configMap?.TRUST_LIKELYAPPROVAL_LOWER?.trim()?.length !== 0
          ? Number(configMap?.TRUST_LIKELYAPPROVAL_LOWER)
          : null;
      const lowerLimitValue =
        configMap?.TRUST_POSSIBLEREJECT_UPPER?.trim()?.length !== 0
          ? Number(configMap?.TRUST_POSSIBLEREJECT_UPPER)
          : null;
      const middleRange = String(configMap?.TRUST_POSSIBLEAPPROVAL);

      const { score: riskScore, kind: riskKind } =
        getRiskMeterParamsForConfidence(
          Number(nodeData?.TRUST_SCORE) ||
            Number(nodeData?.JRMRULES?.TRUST_SCORE),
          upperLimitValue,
          lowerLimitValue,
          middleRange
        );

      if (riskKind === 'info') return null;

      return (
        <Chip
          className='Tile-titleHeadingTrustWrapper--riskChip'
          classnames={{
            contentContainerClassName:
              'Tile-titleHeadingTrustWrapper--contentContainer',
          }}
          color={riskKind}
          size='xSmall'
          label={riskScore}
          leftIcon={<Icon kind={getTrustChipIconKind(riskKind)} />}
        />
      );
    }

    const { kind: riskKind } = getStateForPAA(nodeData?.JRMRULES?.rmd);

    if (riskKind === 'info' || Number(chipRiskScore) === 0) return null;

    if (chipRiskScore)
      return (
        <Chip
          color={riskKind}
          size='xSmall'
          label={chipRiskScore}
          leftIcon={<Icon kind={getTrustChipIconKind(riskKind)} />}
        />
      );

    return null;
  };

  const getTitleColData = () => {
    if (isEndPointGroup)
      return (
        <Typography kind='h4' color='--color-foreground-neutral-medium'>
          {nodeData?.ENDPOINTNAME || 'NOT PROVIDED'}
        </Typography>
      );

    return (
      <>
        <Box tag='div' className='Tile-titleHeadingTrustWrapper'>
          <Typography
            kind='body3'
            mobile='body4-m'
            color='--color-foreground-secondary-bold'>
            {getTileKindHeading()}
          </Typography>
          {isTrustChipVisible && getRiskChip()}
        </Box>
        <Typography
          kind='h3'
          color={
            isNoValueTitle
              ? '--color-foreground-neutral-medium'
              : '--color-foreground-secondary-bold'
          }
          tooltipConfigs={{
            maxWidth: '20rem',
            openDelay: TOOLTIP_DELAY_IN_MS_TILE,
            isPortalEnabled: true,
          }}
          lineLimit={
            !isMobileXlargeWidth ||
            (isMobileXlargeWidth && !isTitleExpanded && isTextOverflowing)
              ? 1
              : undefined
          }>
          {titleValue}
        </Typography>
        {isMobileXlargeWidth && isTextOverflowing && (
          <button
            type='button'
            className='Tile-expandButton'
            onClick={(e) => {
              e.stopPropagation();
              const newExpandedState = !isTitleExpanded;

              setIsTitleExpanded(newExpandedState);
              setExpandedState(stableKey, newExpandedState);
              onTitleExpand(nodeData?.id, newExpandedState);
            }}
            aria-label={isTitleExpanded ? 'Collapse text' : 'Expand text'}>
            <Icon
              kind={isTitleExpanded ? 'ChevronUp' : 'ChevronDown'}
              size='small'
            />
          </button>
        )}
      </>
    );
  };

  const maxCountOfColoumns = columnPickerList
    ? Math.max(
        ...Object.keys(columnPickerList).map(
          (column) => Object.keys(columnPickerList[column]).length
        )
      )
    : 0;

  const shouldShowArsButton = () => {
    if (
      nodeData?.CERT_ENTITLEMENT_VALUEKEY !== '-4' &&
      nodeData?.arstaskkeyflag
    ) {
      return true;
    }

    return false;
  };

  // Column resize effect - handles mouse move and mouse up events during resize
  useEffect(() => {
    if (!resizingCol) return undefined;

    const handleMouseMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const leftHoverSide = tileRef.current?.querySelector(
        '.Tile-leftHoverSide'
      );
      const attributeCols = tileRef.current?.querySelector(
        '.Tile-attributeCols'
      );
      const leftHoverSideWidth =
        leftHoverSide?.offsetWidth || window.innerWidth;
      const delta = e.clientX - resizingCol.startX;
      const calculatedWidth = resizingCol.startWidth + delta;
      const minWidth = resizingCol.colIndex === 0 ? MIN_TITLE_WIDTH : 100;

      // Calculate max width based on column type and available space
      let maxWidth;

      if (resizingCol.colIndex === 0) {
        // Title column: limit to left hover side width minus buffer
        maxWidth = leftHoverSideWidth - 100;
      } else {
        // Attribute columns: limit to remaining space in attributeCols container
        const attributeElements =
          attributeCols?.querySelectorAll('.Tile-attribute');
        const currentAttribute = attributeElements?.[resizingCol.colIndex - 1];
        const attributeRect = currentAttribute?.getBoundingClientRect();
        const attributeColsRect = attributeCols?.getBoundingClientRect();
        const remainingSpace = attributeColsRect
          ? attributeColsRect.right - (attributeRect?.left || 0)
          : 0;

        maxWidth = remainingSpace - 40;
      }

      const newWidth = Math.min(maxWidth, Math.max(minWidth, calculatedWidth));

      if (resizingCol.colIndex === 0) {
        onResizeColumn(
          resizingCol.colIndex,
          newWidth,
          newWidth - defaultTitleWidth
        );
      } else {
        onResizeColumn(resizingCol.colIndex, newWidth);
      }
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Block tile clicks for 200ms after resize to prevent accidental side panel opening
      justResizedRef.current = true;
      setResizingCol(null);
      document.body.style.userSelect = '';
      setTimeout(() => {
        justResizedRef.current = false;
      }, 200);
    };

    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [resizingCol, onResizeColumn, defaultTitleWidth]);

  // Initiates column resize on mouse down
  const handleResizeStart = (e, colIndex, currentWidth) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingCol({ colIndex, startX: e.clientX, startWidth: currentWidth });
    document.body.style.userSelect = 'none';
  };

  // Prevents default behavior for keyboard events on resize handle
  const handleResizeKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // TODO: Hidden Attribute Column Data is still rendered in overflow. Consider removing from DOM instead to reduce divs in DOM by ~5 per overflown attribute.
  const getAttributeColData = () => {
    let tempFilledColsCount = 0;
    const dataKeysWithOutFixedCol = Object.keys(data)?.filter(
      (key) => key !== fixedColKey
    );

    if (!dataKeysWithOutFixedCol || dataKeysWithOutFixedCol?.length === 0)
      return <Box tag='div' className='Tile-attribute' />;

    return (
      <>
        {dataKeysWithOutFixedCol?.map((key, index) => {
          let isNoValue =
            data[key] === null ||
            data[key] === undefined ||
            typeof data[key] === 'object' ||
            (typeof data[key] === 'string' &&
              data?.[key]?.trim()?.length === 0);

          let attributeValue = isNoValue ? '-' : `${data?.[key]}`;

          if (key === 'JRMRULES' && nodeData?.JSON_JRMRULESAVAILABLE) {
            attributeValue = nodeData?.JSON_JRMRULESAVAILABLE;
            isNoValue = false;
          }

          if (!isNoValue && DATE_COLUMNS.includes(key)) {
            isNoValue = false;
            attributeValue = moment
              .utc(attributeValue)
              .local()
              .format(`${globalDateFormat?.toUpperCase()}`);
          }

          if (
            typeof attributeValue === 'string' &&
            attributeValue?.includes('<br')
          )
            if (
              typeof attributeValue === 'string' &&
              attributeValue?.includes('<br')
            )
              attributeValue = attributeValue?.replace(/<br\s*\/?>/gm, '\n');
          attributeValue = attributeValue?.replace(/<br\s*\/?>/gm, '\n');

          tempFilledColsCount = index;
          const colIndex = index + 1;
          const customWidth = columnWidths[colIndex] || 160;

          return (
            <Box
              key={key}
              tag='div'
              className='Tile-attribute'
              style={{
                width: customWidth,
              }}>
              <ViewportTypography
                kind='body3'
                color='--color-foreground-neutral-medium'
                tooltipConfigs={{
                  maxWidth: '20rem',
                  openDelay: TOOLTIP_DELAY_IN_MS_TILE,
                  isPortalEnabled: true,
                }}
                lineLimit={1}>
                {messages[columnLabelKeys[key]] ||
                  localize(intl, getMessageKey(key))}
              </ViewportTypography>
              {ARS_KEYS.includes(key) && shouldShowArsButton() ? (
                <AccessRequestHistoryButton />
              ) : (
                <ViewportTypography
                  kind='body2'
                  color='--color-foreground-secondary-bold'
                  tooltipConfigs={{
                    maxWidth: '20rem',
                    openDelay: TOOLTIP_DELAY_IN_MS_TILE,
                    isPortalEnabled: true,
                  }}
                  lineLimit={1}>
                  {attributeValue}
                </ViewportTypography>
              )}
              {showResizeHandles &&
                (isGroupByActive && tileLevel === 1
                  ? isFirstGroup || isStuck
                  : !isGroupByActive && (isFirstTile || isStuck)) && (
                  <Typography
                    tag='span'
                    tooltipConfigs={{
                      content: 'Drag to resize columns',
                      isPortalEnabled: true,
                    }}>
                    <button
                      type='button'
                      className='Tile-resizeHandle Tile-resizeHandle--attribute'
                      aria-label='Drag to resize columns'
                      onMouseDown={(e) =>
                        handleResizeStart(e, colIndex, customWidth)
                      }
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={handleResizeKeyDown}>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'>
                        <path d={EXPAND_ICON_PATH} fill='#212328' />
                      </svg>
                    </button>
                  </Typography>
                )}
            </Box>
          );
        })}
        {[
          ...Array(
            Math.max(0, maxCountOfColoumns - tempFilledColsCount + 1)
          ).keys(),
        ].map((key) => (
          <Box key={key} tag='div' className='Tile-attribute' />
        ))}
      </>
    );
  };

  const handleMouseLeaveOnScoreMeter = (e) => {
    e.stopPropagation();
    setIsScoreMeterPopoverOpen(false);
  };

  const handleMouseEnterOnScoreMeter = (e) => {
    e.stopPropagation();
    setIsScoreMeterPopoverOpen(true);
  };

  // TODO: Currently there are duplicated usage of risk variables. Consider the implementation of each variable and combine what can be reused vars: riskScore, riskKind, riskLabel.
  const renderScoreMeter = () => {
    if (
      (isTrustScoreEnabled &&
        (certsKind === COL_FILTER_HEADER_ENTITLEMENT ||
          certsKind === COL_FILTER_HEADER_ROLE)) ||
      certsKind === COL_FILTER_HEADER_ACCOUNT
    ) {
      // Check if score coming from confidence
      if (isTrustScoreEnabled?.enableCertificationTrustModel) {
        const upperLimitValue =
          configMap?.TRUST_LIKELYAPPROVAL_LOWER?.trim()?.length !== 0
            ? Number(configMap?.TRUST_LIKELYAPPROVAL_LOWER)
            : null;
        const lowerLimitValue =
          configMap?.TRUST_POSSIBLEREJECT_UPPER?.trim()?.length !== 0
            ? Number(configMap?.TRUST_POSSIBLEREJECT_UPPER)
            : null;
        const middleRange = String(configMap?.TRUST_POSSIBLEAPPROVAL);

        const {
          score: riskScore,
          kind: riskKind,
          label: riskLabel,
        } = getRiskMeterParamsForConfidence(
          Number(nodeData?.TRUST_SCORE) ||
            Number(nodeData?.JRMRULES?.TRUST_SCORE),
          upperLimitValue,
          lowerLimitValue,
          middleRange
        );

        if (riskKind === 'info')
          return <Box tag='div' className='Tile-placeholderScore--blank' />;

        return (
          <Popover
            isPortalEnabled
            placement='left-start'
            trigger={
              <div
                className='Tile-scoreMeterWrapper'
                onMouseEnter={(e) => handleMouseEnterOnScoreMeter(e)}
                onMouseLeave={(e) => handleMouseLeaveOnScoreMeter(e)}>
                <RiskScore
                  kind={riskKind}
                  size='small'
                  value={riskScore}
                  label={riskLabel}
                  shouldShowZero
                />
              </div>
            }
            popoverContent={
              <Box tag='div' className='Tile-scoreMeterPlaceHolder'>
                <TrustJustification
                  isTrustScoreEnabled={isTrustScoreEnabled}
                  nodeData={nodeData}
                  certsKind={certsKind}
                />
              </Box>
            }
            isOpen={isScoreMeterPopoverOpen}
            setIsOpen={setIsScoreMeterPopoverOpen}
          />
        );
      }

      const riskScoreNumber = Number(nodeData?.JRMRULES?.cnf || '0');
      const { kind: riskKind, label: riskLabel } = getStateForPAA(
        nodeData?.JRMRULES?.rmd
      );

      if (riskKind === 'info' || Number(riskScoreNumber) === 0)
        return <Box tag='div' className='Tile-placeholderScore--blank' />;

      return (
        <Popover
          isPortalEnabled
          placement='left-start'
          trigger={
            <div
              className='Tile-scoreMeterWrapper'
              onMouseEnter={(e) => handleMouseEnterOnScoreMeter(e)}
              onMouseLeave={(e) => handleMouseLeaveOnScoreMeter(e)}>
              <RiskScore
                kind={riskKind}
                size='small'
                value={riskScoreNumber}
                label={riskLabel}
              />
            </div>
          }
          popoverContent={
            <Box tag='div' className='Tile-scoreMeterPlaceHolder'>
              <TrustJustification
                isTrustScoreEnabled={isTrustScoreEnabled}
                nodeData={nodeData}
                certsKind={certsKind}
              />
            </Box>
          }
          isOpen={isScoreMeterPopoverOpen}
          setIsOpen={setIsScoreMeterPopoverOpen}
        />
      );
    }

    return <Box tag='div' className='Tile-placeholderScore--blank' />;
  };

  const checkForActions = actionButtonsMap && actionButtonsMap.length > 0;
  const shouldRenderActionButtonsGroup = Boolean(
    !isEndPointGroup &&
      (!isSidePanelVisible || (isSidePanelVisible && isTabletWidth)) &&
      checkForActions
  );

  // Code for touch device for checkbox actions
  let animationFrameId;

  const handlePointerDown = (e) => {
    if (isBulkActionEnabled) {
      setTouchStartPosition({ x: e.clientX, y: e.clientY });
      setIsTouchScrolling(false);
    }
  };

  const handlePointerMove = (e) => {
    if (animationFrameId || !isBulkActionEnabled) return;

    animationFrameId = requestAnimationFrame(() => {
      const deltaX = Math.abs(e.clientX - touchStartPosition.x);
      const deltaY = Math.abs(e.clientY - touchStartPosition.y);

      if (deltaX > SCROLL_THRESHOLD || deltaY > SCROLL_THRESHOLD) {
        setIsTouchScrolling(true);
      }

      animationFrameId = null;
    });
  };

  const handlePointeUp = (e) => {
    if (
      !isTouchScrolling &&
      e.pointerType === 'touch' &&
      !isCheckingIntoSession &&
      isBulkActionEnabled
    ) {
      handleCheckboxChange(
        nodeData?.tracker?.id,
        nodeData?.tracker?.level,
        nodeData?.tracker?.isChecked,
        nodeData?.tracker?.childCount > 0
      );
    }
  };

  return (
    <>
      <section
        ref={tileRef}
        className={tileClasses}
        data-testid={`cert-details-list-level-${level}`}>
        {isBulkActionEnabled && nodeData?.enableAction?.length > 0 ? (
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointeUp}
            className={tileCheckHoverDisplayBtn}>
            {handleCheckboxHoverDisplay()}
          </div>
        ) : (
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointeUp}
            className={tileCheckIconClasses}>
            {getTileCheckIcon(false)}
          </div>
        )}

        <div
          tabIndex={isEndPointGroup ? -1 : 0}
          role='button'
          onClick={handleTileClick}
          onKeyDown={(e) => handleKeyDownTile(e)}
          className={leftHoverSideClasses}
          aria-label='Click to open side panel'>
          <Box
            ref={titleColRef}
            tag='div'
            className={titleColClasses}
            style={{
              width:
                columnWidths[0] !== undefined
                  ? defaultTitleWidth + columnWidths[0]
                  : defaultTitleWidth,
            }}>
            {getTitleColData()}
            {showResizeHandles &&
              (isGroupByActive && tileLevel === 1
                ? isFirstGroup || isStuck
                : !isGroupByActive && (isFirstTile || isStuck)) && (
                <>
                  <Typography
                    tag='span'
                    tooltipConfigs={{
                      content: 'Drag to resize columns',
                      isPortalEnabled: true,
                    }}>
                    <button
                      type='button'
                      tabIndex='-1'
                      className='Tile-resizeHandle'
                      aria-label='Drag to resize columns'
                      onMouseDown={(e) =>
                        handleResizeStart(
                          e,
                          0,
                          columnWidths[0] !== undefined
                            ? defaultTitleWidth + columnWidths[0]
                            : defaultTitleWidth
                        )
                      }
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={handleResizeKeyDown}>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'>
                        <path d={EXPAND_ICON_PATH} fill='#212328' />
                      </svg>
                    </button>
                  </Typography>
                  {/* COACH MARK DISABLED - Uncomment below to re-enable */}
                  {/* {showCoachMark && !isMobileXlargeWidth && (
                    <Popover
                      isPortalEnabled
                      placement='right'
                      isOpen={showCoachMark}
                      trigger={<div />}
                      popoverContent={
                        <Box tag='div' className='Tile-coachMarkContent'>
                          <Typography kind='body2'>
                            Drag to resize columns
                          </Typography>
                          <Button
                            kind='filled'
                            size='small'
                            onClick={handleCoachMarkDismiss}>
                            Got it
                          </Button>
                        </Box>
                      }
                    />
                  )} */}
                </>
              )}
          </Box>
          <Box tag='div' className='Tile-attributeCols'>
            {getAttributeColData()}
          </Box>
          {!isMobileXlargeWidth && !isSACampaign && (
            <Box tag='div' className='Tile-trustScore'>
              {renderScoreMeter()}
            </Box>
          )}
        </div>
        {/* Action Buttons */}
        {shouldRenderActionButtonsGroup && (
          <div className={actionButtonGroupClasses}>
            <CertsActionButtonsGroup
              kind={ACTION_BUTTON_GROUP_KINDS.TILE}
              nodeData={nodeData}
              filteredNodeData={data}
              isActionGroupExpanded={isActionGroupExpanded}
              setIsActionGroupExpanded={setIsActionGroupExpanded}
              setActionPopoverOpenId={setActionPopoverOpenId}
              setMergedLists={setMergedLists}
              isScrolling={isScrolling}
              handleAutoSelectParent={handleAutoSelectParent}
              groupTooltipText={
                shouldShowGroupToolTip
                  ? 'Options for multiple-item selections are available in the toolbar below.'
                  : null
              }
              isGroupDisabled={shouldDisableActionBtns}
              isSaviResponded={isSaviResponded}
              saviRespondedReason={saviRespondedReason}
              getTileCheckIcon={getTileCheckIcon}
              titleValue={titleValue}
              getTileKindHeading={getTileKindHeading}
              handleTileClick={handleTileClick}
              bulkSelectInfoForDrawer={{
                isChecked: nodeData?.tracker?.isChecked,
                childCount: nodeData?.tracker?.childCount,
                isBulkActionEnabled,
                isAtleastOneChildChecked:
                  nodeData?.tracker?.isAtleastOneChildChecked,
                handleBulkSelectFromDrawer: () =>
                  handleCheckboxChange(
                    nodeData?.tracker?.id,
                    nodeData?.tracker?.level,
                    nodeData?.tracker?.isChecked,
                    nodeData?.tracker?.childCount > 0
                  ),
              }}
            />
          </div>
        )}
        {/* Overlay */}
        <Box tag='span' className='Tile-overlay' />
      </section>
      {isLevelZero && isStuck && (
        <Box tag='span' className='Tile-fillStuck--0' />
      )}
      {isLevelOne && isStuck && (
        <Box tag='span' className='Tile-fillStuck--1' />
      )}
    </>
  );
}

Tile.propTypes = {
  data: PropTypes.shape({}),
  level: PropTypes.number.isRequired,
  handleSidePanel: PropTypes.func.isRequired,
  handleAutoSelectParent: PropTypes.func.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  setMergedLists: PropTypes.func.isRequired,
  actionPopoverOpenId: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setActionPopoverOpenId: PropTypes.func.isRequired,
  isScrolling: PropTypes.bool.isRequired,
  columnWidths: PropTypes.shape({}),
  onResizeColumn: PropTypes.func,
  showResizeHandles: PropTypes.bool,
  tileLevel: PropTypes.number,
  isFirstTile: PropTypes.bool,
  isFirstGroup: PropTypes.bool,
  isGroupByActive: PropTypes.bool,
  onTitleExpand: PropTypes.func,
};

Tile.defaultProps = {
  data: {},
  actionPopoverOpenId: false,
  columnWidths: {},
  onResizeColumn: () => {},
  showResizeHandles: false,
  tileLevel: 0,
  isFirstTile: false,
  isFirstGroup: false,
  isGroupByActive: false,
  onTitleExpand: () => {},
};

export default React.memo(Tile);
